// EDV Next 0.10.9 - PURE helpers for web module navigation.
// This file must remain JSX-free so it can be imported and executed by
// Node --experimental-strip-types (no JSX transform available). React
// components live in `modules.tsx`.

import type { SessionMe } from '@edv-next/contracts';

export type ModuleKey = 'lube' | 'ac' | 'fmea';

/** Fixed display order (stable across sessions). */
export const MODULE_KEYS: readonly ModuleKey[] = ['lube', 'ac', 'fmea'] as const;

export const MODULE_LABEL: Record<ModuleKey, string> = {
  lube: 'Lube Manager',
  ac:   'Asset Criticality',
  fmea: 'FMEA',
};

export const STORAGE_KEY = 'edv-next:active-module';

/** Pure derivation: entitled modules for the active organization (can_read=true). */
export function entitledModules(me: SessionMe | null, orgId: string | null): ModuleKey[] {
  if (!me || !orgId) return [];
  const allowed = new Set<string>();
  for (const e of me.entitlements) {
    if (e.organization_id !== orgId) continue;
    if (!e.can_read) continue;
    allowed.add(e.module_key);
  }
  return MODULE_KEYS.filter(k => allowed.has(k));
}

/** Validate stored module value against the current entitled list. */
export function validateStoredModule(raw: string | null, entitled: readonly ModuleKey[]): ModuleKey | null {
  if (!raw) return null;
  if (!(MODULE_KEYS as readonly string[]).includes(raw)) return null;
  if (!(entitled as readonly string[]).includes(raw)) return null;
  return raw as ModuleKey;
}

/** Deterministic fallback: first entitled module, or null. */
export function firstEntitled(entitled: readonly ModuleKey[]): ModuleKey | null {
  return entitled.length > 0 ? entitled[0]! : null;
}

/** Bounded, non-sensitive read/write of the persisted module. */
export function readStoredModule(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return typeof v === 'string' ? v : null;
  } catch { return null; }
}
export function writeStoredModule(k: ModuleKey | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (k === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, k);
  } catch { /* private mode / disabled storage */ }
}
